import type { RejectReason } from './types.ts';

/** Longest payload accepted from any provider. GS1 DataMatrix payloads are well under this. */
export const MAX_SCAN_LENGTH = 1024;
export const MAX_SYMBOLOGY_LENGTH = 40;

/** GS (0x1D) separates GS1 element strings in a DataMatrix. Kept: a later marking-code resolver needs it. */
const GS = 0x1d;

export type NormalizeOptions = {
  readonly maxLength?: number;
  /** Strip an AIM symbology identifier (`]C0`, `]E0`, `]d2`...) and report it as the symbology. Default true. */
  readonly stripAimIdentifier?: boolean;
};

export type NormalizeResult =
  | { readonly ok: true; readonly value: string; readonly symbology?: string }
  | { readonly ok: false; readonly reason: Extract<RejectReason, 'EMPTY' | 'CONTROL_ONLY' | 'TOO_LONG' | 'MALFORMED_UNICODE'> };

// Only identifiers whose meaning is standardised (ISO/IEC 15424 code characters). Anything else keeps no label.
const AIM_SYMBOLOGY: Readonly<Record<string, string>> = {
  A: 'CODE39',
  C: 'CODE128',
  E: 'EAN_UPC',
  F: 'CODABAR',
  G: 'CODE93',
  I: 'ITF',
  L: 'PDF417',
  Q: 'QR',
  d: 'DATAMATRIX',
  e: 'GS1_DATABAR',
  z: 'AZTEC',
};

function hasLoneSurrogate(s: string): boolean {
  for (let i = 0; i < s.length; i += 1) {
    const c = s.charCodeAt(i);
    if (c >= 0xd800 && c <= 0xdbff) {
      const n = s.charCodeAt(i + 1);
      if (!(n >= 0xdc00 && n <= 0xdfff)) return true;
      i += 1;
    } else if (c >= 0xdc00 && c <= 0xdfff) {
      return true;
    }
  }
  return false;
}

function isStrippedCodeUnit(c: number): boolean {
  if (c === GS) return false;
  if (c <= 0x1f || c === 0x7f) return true; // C0 + DEL
  if (c >= 0x80 && c <= 0x9f) return true; // C1
  if (c >= 0x200b && c <= 0x200f) return true; // zero-width, LRM/RLM
  if (c >= 0x2028 && c <= 0x202e) return true; // line/paragraph separators, bidi embeddings
  if (c >= 0x2060 && c <= 0x2069) return true; // word joiner, invisible operators, bidi isolates
  if (c === 0x00ad || c === 0x180e) return true; // soft hyphen, Mongolian vowel separator
  if (c >= 0xfff9 && c <= 0xfffb) return true; // interlinear annotation anchors
  if (c === 0xfeff) return true; // BOM / zero-width no-break space
  return false;
}

/** Cleans a vendor-supplied symbology label to a short token, or undefined. */
export function normalizeSymbology(raw: unknown): string | undefined {
  if (typeof raw !== 'string') return undefined;
  const s = raw.trim().slice(0, MAX_SYMBOLOGY_LENGTH).toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  return s === '' ? undefined : s;
}

/**
 * The single normalisation every provider's output passes through. Never throws; never logs; the payload is
 * never altered beyond removing characters that cannot be part of a barcode (case is preserved).
 */
export function normalizeScanValue(raw: unknown, opts: NormalizeOptions = {}): NormalizeResult {
  const maxLength = opts.maxLength ?? MAX_SCAN_LENGTH;
  if (typeof raw !== 'string' || raw.length === 0) return { ok: false, reason: 'EMPTY' };
  // Hard cap before any per-character work: a flood of megabyte payloads must stay cheap.
  if (raw.length > maxLength * 4) return { ok: false, reason: 'TOO_LONG' };
  if (hasLoneSurrogate(raw)) return { ok: false, reason: 'MALFORMED_UNICODE' };
  let out = '';
  for (let i = 0; i < raw.length; i += 1) {
    if (!isStrippedCodeUnit(raw.charCodeAt(i))) out += raw[i];
  }
  out = out.trim();
  // A leading/trailing GS is framing, not data.
  while (out.length > 0 && out.charCodeAt(0) === GS) out = out.slice(1);
  while (out.length > 0 && out.charCodeAt(out.length - 1) === GS) out = out.slice(0, -1);
  out = out.trim();
  if (out === '') return { ok: false, reason: raw.trim() === '' ? 'EMPTY' : 'CONTROL_ONLY' };

  let symbology: string | undefined;
  if (opts.stripAimIdentifier !== false) {
    const m = /^\](.)[0-9A-Za-z]/.exec(out);
    if (m) {
      symbology = AIM_SYMBOLOGY[m[1]!];
      out = out.slice(3).trim();
    }
  }
  if (out === '') return { ok: false, reason: 'EMPTY' };
  if (out.length > maxLength) return { ok: false, reason: 'TOO_LONG' };
  return symbology === undefined ? { ok: true, value: out } : { ok: true, value: out, symbology };
}
